import React, { useEffect } from "react";
import { TextField, Switch, Box, Grid, Backdrop, Button } from "@material-ui/core";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import { makeStyles, Theme } from "@material-ui/core/styles";
import EditIcon from "@material-ui/icons/Edit";
import { useFormik } from "formik";
import { object, string, number } from "yup";
import {
  addNewPaymentType,
  updatePaymentTypeRow,
  getAllPaymentTypes,
} from "src/app-redux/configuration/actions/configurationActions";
import {
  selectAllPaymentTypeRowsList,
  loader,
} from "src/app-redux/configuration/configurationSlice";
import { useAppDispatch, useAppSelector } from "src/app-redux/hooks";
import { IAddPaymentType } from "src/common/typings/configuration";
import NewCommonButton from "src/components/CommonButton/NewCommonButton";
import CommonButton from "../../../../../../components/CommonButton/CommonButton";
import { columns } from "./columns";
import CommonAutoCompleteField from "src/components/CommonAutoCompleteField/CommonAutoCompleteField";
import { Prompt } from "react-router-dom";
import CommonMaterialGrid from "src/components/CommonXGrid/CommonMaterialGrid";
import { Check, Close } from "@material-ui/icons";
import { Tooltip } from "@mui/material";
import CommonLoader from "src/components/Loader/CommonLoader";

const useStyles = makeStyles((theme: Theme) => ({
  formRow: {
    marginBottom: "0px",
  },
  backdrop: {
    zIndex: theme.zIndex.modal + 1,
    color: "#fff",
  },
  tabCardContent: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    overflow: "auto",
  },

  UserMainPanel: {
    width: "100%",
    float: "left",
    padding: "0px 20px",
    paddingRight: 0,
  },
  mainConainer: { marginTop: "0px" },
  left0: { paddingLeft: "0px!important" },
  right0: { paddingRight: "0px!important" },
  left20: { paddingLeft: "20px!important" },
  normal: { paddingLeft: "8px!important", paddingRight: "8px!important" },
}));

export interface IForm extends IAddPaymentType {}

interface Props {}

export function InputFields({}: Props): React.ReactElement {
  const classes = useStyles();
  const dispatch = useAppDispatch();

  const [displayColumns, setDisplayColumns] = React.useState<any>(columns);
  const [isEditMode, setisEditMode] = React.useState<boolean>(false);
  const [selectedRecord, setSelectedRecord] = React.useState<any>(null);

  const loading = useAppSelector(loader);
  const allPaymentTypeRows = useAppSelector(selectAllPaymentTypeRowsList);
  const allCurrencies = ["PKR"];
  const allPaymentTypes = [
    { id: 0, name: "Variable Charges" },
    { id: 1, name: "Fixed Charges" },
  ];

  // Form Submission
  const validationSchema = object({
    name: string().required("This field is required").max(50, "Maximum 50 digits are allowed."),
    amount: number()
      .required("This field is required")
      .min(0, "Minimum 0 is allowed.")
      .max(9999999999, "Maximum 10 digits are allowed."),
    currency: string().required("This field is required"),
    isFixed: number().required("This field is required"),
  });
  const formik = useFormik<IAddPaymentType>({
    enableReinitialize: true,
    initialValues: {
      id: "",
      name: "",
      amount: 0,
      currency: "PKR",
      isFixed: 0,
      isActive: false,
    },
    validationSchema: validationSchema,
    onSubmit: async (values, actions) => {
      let res;
      if (isEditMode) res = await dispatch(updatePaymentTypeRow(values));
      else res = await dispatch(addNewPaymentType(values));
      if (res?.payload) {
        dispatch(getAllPaymentTypes());
        actions.setSubmitting(false);
        actions.resetForm();
        setisEditMode(false);
        setIsDirty(false);
        setSelectedRecord(null);
      }
    },
  });
  // End Form Submission
  const handleUpdateClick = (params: any) => {
    setisEditMode(true);
    setSelectedRecord(params);
    formik.setFieldValue("id", params.id);
    formik.setFieldValue("name", params.name);
    formik.setFieldValue("amount", params.amount);
    formik.setFieldValue("currency", params.currency.toUpperCase());
    formik.setFieldValue("isFixed", params.isFixed);
    formik.setFieldValue("isActive", params.isActive);
  };
  useEffect(() => {
    dispatch(getAllPaymentTypes());
  }, []);

  useEffect(() => {
    if (allPaymentTypeRows.length) {
      const transformedColumns: any = columns.map((column: any) =>
        column.accessorKey === "actions"
          ? {
              ...column,
              Cell: function customCell({ row }: any) {
                return (
                  <>
                    <NewCommonButton
                      onClick={() => {
                        setIsDirty(true);
                        handleUpdateClick(row.original);
                      }}
                      icon={<EditIcon />}
                      isEdit={true}
                    >
                      Edit
                    </NewCommonButton>
                  </>
                );
              },
            }
          : column.accessorKey === "amount"
          ? {
              ...column,
              Cell: function customCell({ row }: any) {
                return ` ${
                  row.original.currency.toUpperCase() || "PKR "
                } ${row.original.amount.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
              },
            }
          : column.accessorKey === "isActive"
          ? {
              ...column,
              Cell: function customCell({ row }: any) {
                return (
                  <React.Fragment>
                    <Box>
                      <Tooltip title={row.original?.isActive ? "Yes" : "No"}>
                        <Button style={{ minWidth: "30px", padding: "4px", color: "gray" }}>
                          {row.original?.isActive ? <Check /> : <Close />}
                        </Button>
                      </Tooltip>
                    </Box>
                  </React.Fragment>
                );
              },
            }
          : column
      );

      setDisplayColumns(transformedColumns);
    }
  }, [allPaymentTypeRows]);

  const [isDirty, setIsDirty] = React.useState<boolean>(false);
  useEffect(() => {
    window.onbeforeunload = function (e: Event) {
      if (isDirty) {
        e.preventDefault();
        return "Changes you made may not be saved.";
      }
    };
  }, [isDirty]);
  function handleChange(e: any) {
    formik.handleChange(e);
    e.target.value || JSON.stringify(formik.values) !== JSON.stringify(formik.initialValues)
      ? setIsDirty(true)
      : setIsDirty(false);
  }

  return (
    <>
      <Prompt when={isDirty} message="Changes you made may not be saved." />
      <form onSubmit={formik.handleSubmit}>
        <Backdrop className={classes.backdrop} open={loading}>
          <CommonLoader />
        </Backdrop>
        <Grid container direction="row" alignItems="flex-start">
          {/* First Section */}
          <>
            <Grid className={classes.UserMainPanel}>
              <Grid
                // Container
                container
                className={classes.mainConainer}
                direction="row"
                alignItems="center"
                spacing={2}
              >
                <Grid container item xs={12} spacing={2}>
                  <Grid item xs={12} md={6} lg={3} style={{ paddingRight: 0 }}>
                    <Box>
                      <TextField
                        className="customTextField"
                        type="string"
                        required
                        size="small"
                        fullWidth
                        variant="outlined"
                        InputLabelProps={{
                          shrink: true,
                        }}
                        label="Type of Payment"
                        name="name"
                        placeholder="Add Type of Payment"
                        disabled={false}
                        value={formik.values.name}
                        onChange={handleChange}
                        error={formik.touched.name && Boolean(formik.errors.name)}
                        helperText={formik.touched.name && formik.errors.name}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={3} lg={2} style={{ paddingRight: 0 }}>
                    <Box>
                      <TextField
                        className="customTextField"
                        type="number"
                        required
                        size="small"
                        fullWidth
                        variant="outlined"
                        InputLabelProps={{
                          shrink: true,
                        }}
                        InputProps={{
                          inputProps: { min: 0 },
                        }}
                        label="Charges"
                        name="amount"
                        placeholder="Enter Payment Amount"
                        disabled={false}
                        value={formik.values.amount}
                        onChange={handleChange}
                        error={formik.touched.amount && Boolean(formik.errors.amount)}
                        helperText={formik.touched.amount && formik.errors.amount}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={3} lg={2} style={{ paddingRight: 0 }}>
                    <Box>
                      <CommonAutoCompleteField
                        cssClassName="customTextField"
                        disabled={false}
                        label="Currency"
                        name="currency"
                        placeholder="Select Currency"
                        disableClearable={true}
                        optionsType={allCurrencies}
                        getOptionLabel={(option) => option}
                        required
                        value={
                          allCurrencies.find((st: any) => st === formik.values.currency) || null
                        }
                        onChange={(option: any) => {
                          option && option !== "" ? formik.setFieldValue("currency", option) : "";
                        }}
                        error={formik.touched.currency && Boolean(formik.errors.currency)}
                        helperText={formik.touched.currency && formik.errors.currency}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={7} lg={2} style={{ paddingRight: 0 }}>
                    <Box>
                      <CommonAutoCompleteField
                        cssClassName="customTextField"
                        disabled={false}
                        label="Variable/Fixed"
                        name="isFixed"
                        placeholder="Select Variable/Fixed"
                        disableClearable={true}
                        optionsType={allPaymentTypes}
                        getOptionLabel={(option) => option.name}
                        required
                        value={
                          allPaymentTypes.find((st: any) => st.id === formik.values.isFixed) || null
                        }
                        onChange={(option: any) => {
                          formik.setFieldValue("isFixed", option.id);
                        }}
                        error={formik.touched.isFixed && Boolean(formik.errors.isFixed)}
                        helperText={formik.touched.isFixed && formik.errors.isFixed}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={6} md={3} lg={1} style={{ paddingRight: 0 }}>
                    <Box>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={Boolean(formik.values.isActive)}
                            onChange={formik.handleChange}
                            name="isActive"
                            color="primary"
                          />
                        }
                        label="Active"
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={6} md={2} lg={2} style={{ paddingRight: 0 }}>
                    <Box mb={1} mt={1} className={"headerButtonsConfig"} style={{ float: "right" }}>
                      {/* <CommonButton
                        // isGreen
                        cssClass="customSubmitBtn"
                        onClick={() => {
                          setisEditMode(false);
                          setIsDirty(false);
                          formik.resetForm();
                          setSelectedRecord(null);
                        }}
                      >
                        Reset
                      </CommonButton> */}
                      <CommonButton
                        isGreen
                        cssClass="customSubmitBtn"
                        disable={
                          selectedRecord
                            ? (formik.values.name == selectedRecord.name &&
                                formik.values.amount == selectedRecord.amount &&
                                formik.values.currency == selectedRecord.currency &&
                                formik.values.isActive == selectedRecord.isActive) ||
                              formik.values.name.toLowerCase() === "other" ||
                              formik.values.name.toLowerCase() === "others" ||
                              formik.values.name.toLowerCase() === "0ther" ||
                              formik.values.name.toLowerCase() === "0thers"
                            : !formik.isValid ||
                              formik.values.name.toLowerCase() === "other" ||
                              formik.values.name.toLowerCase() === "others" ||
                              formik.values.name.toLowerCase() === "0ther" ||
                              formik.values.name.toLowerCase() === "0thers" ||
                              JSON.stringify(formik.values) === JSON.stringify(formik.initialValues)
                        }
                        onClick={() => {
                          formik.submitForm();
                        }}
                      >
                        {isEditMode ? "Update" : "Add"}
                      </CommonButton>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <CommonMaterialGrid
                      defaultPerPageRecord={10}
                      height={400}
                      rows={allPaymentTypeRows || []}
                      columns={displayColumns}
                    />
                  </Grid>
                </Grid>
                {/* End Inner Container */}
              </Grid>
              {/* End Outer Container */}
            </Grid>
          </>
        </Grid>
      </form>
    </>
  );
}
